<template>
  <b-overlay
    :show="loading"
    rounded
    opacity="0.7"
    spinner-small
    spinner-variant="primary"
  >
    <div class="order-service-general-quotation">
      <!-- Форма редактирования котировки -->
      <div v-if="shouldShowForm && isAgent">
        <p>Заполните форму для запроса котировки по услуге. Это поможет менеджерам подготовить точное предложение.</p>
        <b-form>
          <!-- Тип операции -->
          <b-form-group label="Тип операции" description="Выберите тип операции">
            <b-form-radio-group
              v-model="quotation.import_export_type"
              :options="[
                { value: 'import', text: 'Импорт' },
                { value: 'export', text: 'Экспорт' }
              ]"
            ></b-form-radio-group>
          </b-form-group>

          <!-- Описание услуги -->
          <b-form-group label="Описание услуги" description="Опишите детали услуги, требования и спецификации">
            <b-form-textarea 
              v-model="quotation.service_description" 
              size="sm" 
              rows="4"
              placeholder="Опишите детали услуги, требования, спецификации, условия выполнения..."
              required
            ></b-form-textarea>
            <small v-if="!quotation.service_description" class="text-danger">Это поле обязательно для заполнения.</small>
          </b-form-group>

          <b-form-row>
            <b-col cols="6">
              <!-- Сроки выполнения -->
              <b-form-group label="Ориентировочная дата таможенного оформления">
                <b-form-input 
                  v-model="quotation.deadline_start" 
                  type="date" 
                  size="sm"
                  placeholder="Введите дату"
                ></b-form-input>
              </b-form-group>

              <!-- Периодичность поставок -->
              <b-form-group label="Периодичность поставок" description="Укажите периодичность поставок">
                <b-form-input 
                  v-model="quotation.delivery_frequency" 
                  type="text" 
                  size="sm"
                  placeholder="Например: еженедельно, ежемесячно, по требованию..."
                  required
                ></b-form-input>
                <small v-if="!quotation.delivery_frequency" class="text-danger">Это поле обязательно для заполнения.</small>
              </b-form-group>

              <!-- Бюджет (опционально) -->
              <b-form-group label="Ориентировочный бюджет (опционально)">
                <b-form-row>
                  <b-col cols="8">
                    <b-form-input 
                      v-model="quotation.budget_amount" 
                      type="number" 
                      step="0.01"
                      size="sm" 
                      placeholder="Сумма"
                    ></b-form-input>
                  </b-col>
                  <b-col cols="4">
                    <b-form-select
                      v-model="quotation.budget_currency"
                      :options="currencyOptions"
                      size="sm"
                      style="height: 33px !important;"
                    ></b-form-select>
                  </b-col>
                </b-form-row>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <!-- Дополнительные требования -->
              <b-form-group label="Дополнительные требования и условия">
                <b-form-textarea 
                  v-model="quotation.additional_requirements" 
                  size="sm" 
                  rows="10"
                  placeholder="Дополнительные требования, условия, особые пожелания..."
                ></b-form-textarea>
              </b-form-group>
            </b-col>
            <!-- <b-col cols="6">
              <b-form-input 
                v-model="quotation.deadline_end" 
                type="date" 
                size="sm"
                placeholder="Дата завершения"
              ></b-form-input>
            </b-col> -->
          </b-form-row>
        </b-form>
      </div>

      <!-- Текстовое отображение котировки -->
      <div v-else>
        <OrderServiceGeneralQuotationInfo
          v-if="quotation.service_description"
          :quotation="generalQuotationForInfo"
          @orderUpdated="handleOrderUpdated"
        />
      </div>

      <!-- Форма для заполнения ответов менеджеров -->
      <template v-if="order_reference_status_id == 541 && isAgent">
        <div class="mt-4">
          <div class="alert alert-info border-left-info">
            <div class="d-flex align-items-center">
              <div class="mr-3">
                <i class="fas fa-clock fa-2x text-info"></i>
              </div>
              <div>
                <p>
                  <strong>Над котированием уже работают наши менеджеры!</strong>
                </p>
                <p class="mb-0 text-muted">
                  Как только котировка будет готова, Вы сможете увидеть предложение и продолжить работать с клиентом.
                </p>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template v-else-if="quotation.service_description">
        <OrderServiceGeneralQuotationAnswerForms
          v-if="quotationId"
          :order-id="order_id"
          :order-service-id="order_service_id"
          :order-service-quotation-id="quotationId"
          :order-reference-status-id="order_reference_status_id"
          ref="quotationAnswerForms"
          @orderUpdated="handleOrderUpdated"
        />
      </template>

      <!-- Кнопки управления -->
      <hr>
      <b-row v-if="isAgent">
        <b-col cols="12" class="d-flex justify-content-end">
          <div class="text-right">
            <div class="mb-2">
              <b-button 
                v-if="[359, 539, 540].includes(order_reference_status_id)"
                type="button" 
                variant="success"
                :disabled="loading || saving"
                class="ml-2"
                @click.prevent="handleSubmit(false)"
              >Сохранить</b-button>

              <b-button 
                v-if="[359, 539, 540].includes(order_reference_status_id)"
                type="button" 
                variant="primary"
                class="ml-2"
                :disabled="loading || saving || !isFormValid"
                @click.prevent="handleSubmit(true)"
              >
                Сохранить и отправить на котировку
                <i class="fas fa-arrow-right ml-2"></i>
              </b-button>

              <b-button 
                v-if="order_reference_status_id == 542"
                type="link" 
                variant="primary"
                :disabled="loading || saving"
                @click.prevent="changeToQuotationSentStatus(543)"
              >
                Перевести в статус «КП Направлено»
                <i class="fas fa-arrow-right ml-2"></i>
              </b-button>

              <div v-if="order_reference_status_id == 543">
                <b-button 
                  v-if="isAgent || isAdmin"
                  type="link" 
                  variant="secondary"
                  class="ml-2"
                  :disabled="loading || saving"
                  @click.prevent="requestDiscount()"
                >Запросить скидку</b-button>

                <b-button 
                  variant="primary"
                  class="ml-2"
                  :disabled="loading || saving"
                  @click.prevent="askAboutServiceSelection"
                >
                  Перевести в поставку
                  <i class="fas fa-arrow-right ml-2"></i>
                </b-button>
              </div>
            </div>

            <div v-if="[359, 539, 540].includes(order_reference_status_id)">
              <div class="text-right lh12px mb-3">
                <small>
                  <em>После отправки Заявки на котировку,<br />
                    форма запроса на котировку будет закрыта для редактирования.<br />
                    Будьте внимательны при заполнении!
                  </em>
                </small>
              </div>
            </div>
            <div v-if="order_reference_status_id == 541">
              <div class="text-right lh12px mb-3">
                <small>
                  <em>Заявка отправлена на «Получение котировки».<br />
                    Как только котировка будет готова, <br />
                    Заявка автоматически будет переведена в статус «Котировка получена» и вы получите уведомление.
                  </em>
                </small>
              </div>
              
              <!-- Кнопка перевода в статус "Котировка получена" -->
              <div v-if="shouldShowManualStatusChangeButton" class="mt-3">
                <div class="alert alert-warning">
                  <div class="d-flex align-items-center">
                    <div class="mr-3">
                      <i class="fas fa-exclamation-triangle fa-2x text-warning"></i>
                    </div>
                    <div>
                      <p class="mb-1">
                        <strong>Внимание!</strong> Заказ уже ранее находился в статусе "Получение котировки".
                      </p>
                      <p class="mb-2 text-muted">
                        Возможно, требуется ручная смена статуса для продолжения работы.
                      </p>
                      <b-button 
                        variant="primary"
                        size="sm"
                        @click="changeToQuotationReceivedStatus"
                        :disabled="loading || saving"
                      >
                        <i class="fas fa-arrow-right mr-1"></i>
                        Перевести в статус «Котировка получена»
                      </b-button>
                    </div>
                  </div>
                </div>
              </div>
            </div>          
            <div v-if="order_reference_status_id == 542">
              <div class="text-right lh12px mb-3">
                <small>
                  <em>Заявка в статусе «Котировка получена».<br />
                    Ожидается проставление ставок для клиента и перевод в статус «КП Направлено».
                  </em>
                </small>
              </div>
            </div>          
            <div v-if="order_reference_status_id == 543">
              <div class="text-right lh12px mb-3">
                <small>
                  <em>Заявка в статусе «КП Направлено».<br />
                    Как только будет понятно работаем мы по этой Заявке или нет, 
                    <br />выполните перевод Заявки в Выполнение или закройте заявку.
                  </em>
                </small>
              </div>
            </div>          
          </div>
        </b-col>
      </b-row>
    </div>
  </b-overlay>
</template>

<script>
import { mapGetters } from 'vuex';

import { 
  API_POST_ORDER_SERVICES_GENERAL_QUOTATIONS_SAVE, 
  API_POST_ORDER_SERVICES_GENERAL_QUOTATIONS_FIND,
  API_ORDERS_ACTION_HISTORY,
} from '@/constants';

import { tablePrepareData, orderMethods } from '@/mixins';
import { quotationValidationMixin } from '@/mixins/quotationValidationMixin';

import OrderServiceGeneralQuotationInfo from '@/components/orders/orderServiceQuotation/OrderServiceGeneralQuotationInfo.vue';
import OrderServiceGeneralQuotationAnswerForms from '@/components/orders/orderServiceQuotation/OrderServiceGeneralQuotationAnswerForms';

export default {
  props: {
    order_id: { type: Number|String, required: true },
    order_service_id: { type: Number|String, required: true },
    order_reference_status_id: { type: Number|String, required: true },
    order_reference_order_type_id: { type: Number|String, required: true }
  },
  components: {
    OrderServiceGeneralQuotationInfo,
    OrderServiceGeneralQuotationAnswerForms
  },
  mixins: [tablePrepareData, orderMethods, quotationValidationMixin],
  data() {
    return {
      loading: false,
      saving: false,
      quotationId: null,
      orderStatusHistory: [],
      showManualStatusChangeButton: false,
      quotation: {
        service_description: '',
        deadline_start: '',
        deadline_end: '',
        delivery_frequency: '',
        additional_requirements: '',
        budget_amount: null,
        budget_currency: 'RUB',
        import_export_type: 'import'
      },
      currencyOptions: [
        { value: 'RUB', text: 'RUB' },
        { value: 'USD', text: 'USD' },
        { value: 'EUR', text: 'EUR' },
        { value: 'CNY', text: 'CNY' }
      ]
    };
  },
  computed: {
    ...mapGetters(['REFERENCE_PROPERTIES']),
    ...mapGetters("auth", ["currentUser"]),
    
    isAdmin() {
      return this.currentUser.role.includes('administrator');
    },

    isAgent() {
      return this.currentUser.role.includes('agent');
    },

    shouldShowForm() {
      // Форма показывается когда статус 539 или 540 или 359
      const isNotQuotationStatus = this.order_reference_status_id == 359 || this.order_reference_status_id == 539 || this.order_reference_status_id == 540;
      return isNotQuotationStatus;
    },
    
    isFormValid() {
      const requiredFields = [
        this.quotation.service_description,
        this.quotation.import_export_type,
        this.quotation.delivery_frequency
      ];

      return requiredFields.every(field => !!field);
    },

    generalQuotationForInfo() {
      if (!this.quotation || !this.quotation.service_description) {
        return null;
      }
      return {
        id: this.quotationId,
        order_id: this.order_id,
        order_service_id: this.order_service_id,
        order_reference_status_id: this.order_reference_status_id,
        data: this.quotation
      };
    },
    
    // Проверяем количество переходов в статус 541
    status541Count() {
      return this.orderStatusHistory.filter(item => 
        item.history_type === 'order_status' && 
        item.reference_status_id === 541
      ).length;
    },
    
    // Проверяем, нужно ли показать кнопку ручной смены статуса
    shouldShowManualStatusChangeButton() {
      return this.order_reference_status_id === 541 && 
             this.status541Count >= 2 && 
             !this.isAgent;
    }
  },
  watch: {
    isFormValid: {
      handler() {
        this.$emit("isRequestFormValid", this.isFormValid);
      },
      immediate: true
    }
  },  
  mounted() {
    this.loadGeneralQuotation();
    this.loadOrderStatusHistory();
  },
  methods: {
    handleOrderUpdated(data) {
      this.$nextTick(() => {
        this.$emit('orderUpdated', data);
      });
    },
    
    loadGeneralQuotation() {
      this.loading = true;

      const params = {
        order_id: this.order_id,
        order_service_id: this.order_service_id
      };

      api.call('post', API_POST_ORDER_SERVICES_GENERAL_QUOTATIONS_FIND, params).then(({data}) => {
        if (data.success === true) {
          this.quotation = data.quotation.data;
          // Устанавливаем значение по умолчанию для import_export_type, если оно отсутствует
          if (!this.quotation.import_export_type) {
            this.quotation.import_export_type = 'import';
          }
          this.quotationId = data.quotation.id;
          this.$emit("setValuesFromQuotation", data.quotation);
        }
      }).catch((error) => {
        console.error('Ошибка при загрузке данных:', error);
        this.$bvToast.toast('Произошла ошибка при загрузке данных', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true,
        });
      }).finally(() => {
        this.loading = false;
      });
    },
    
    // Загружаем историю статусов заказа
    async loadOrderStatusHistory() {
      try {
        const { data } = await axios.get(`${API_ORDERS_ACTION_HISTORY}/${this.order_id}`);
        this.orderStatusHistory = data || [];
      } catch (error) {
        console.error('Ошибка при загрузке истории статусов:', error);
      }
    },
    
    handleSubmit(isSaveWithStatusChange = false) {
      this.saving = true;

      const payload = {
        order_id: this.order_id,
        order_service_id: this.order_service_id,
        change_status_of_order: isSaveWithStatusChange ? 1 : 0,
        data: this.quotation,
      };

      api.call('post', API_POST_ORDER_SERVICES_GENERAL_QUOTATIONS_SAVE, payload).then(({data}) => {
          // Используем миксин для обработки валидации
          const validationPassed = this.handleApiResponseWithValidation(data, isSaveWithStatusChange);
          
          if (!validationPassed) {
            return;
          }

          let message = 'Данные успешно сохранены';

          if (isSaveWithStatusChange) {
            message = 'Данные успешно сохранены. Статус поставки обновлен на «Получение котировки».';
            this.loading = true;

            setTimeout(() => {
              this.$emit('orderUpdated', {
                orderId: this.order_id,
                message: 'Статус поставки обновлен на «Получение котировки»'
              });
              this.loading = false;
            }, (data.message != "") ? 13000 : 3000);
          } else {
            this.$emit("saved");
          }

          if (data.message != "" && !data.message.includes('ошибок валидации')) {
            this.$bvToast.toast(data.message, {
              title: 'Проблемы со сменой статуса поставки и информированием',
              variant: 'info',
              solid: true,
            });
          }

          this.$bvToast.toast(message, {
            title: 'Успешное сохранение котировки!',
            variant: 'success',
            solid: true,
          });
        }).catch((error) => {
        console.error('Ошибка при сохранении данных:', error);
        this.loading = false;

        this.$bvToast.toast('Произошла ошибка при сохранении данных', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true,
        });
      }).finally(() => {
        this.saving = false;
      });
    },
    
    changeToQuotationSentStatus(status) {
      this.$emit('changeOrderToQuotationSent', this.order_id, status);
      this.$emit("orderUpdated", {
        orderId: this.order_id,
        message: 'Статус заказа обновлен'
      });
    },

    async requestDiscount() {
      try {
        const { value: discountMsg, isConfirmed } = await this.$swal({
          title: 'Запросить скидку',
          input: 'textarea',
          inputPlaceholder: 'Опишите причину и детали скидки (необязательно)',
          inputAttributes: {
            'aria-label': 'Сообщение для запроса скидки'
          },
          showCancelButton: true,
          cancelButtonText: 'Отмена',
          confirmButtonText: 'Отправить',
        });

        if (!isConfirmed) {
          return;
        }

        const updateFields = {
          reference_status_id: 542,
          is_discount_request: true,
          order_service_id: this.order_service_id,
          discount_request_message: discountMsg || ''
        };

        await this.updateAnyOrder(this.order_id, updateFields);
        
        this.$emit("orderUpdated", {
          orderId: this.order_id,
          message: 'Запрос скидки отправлен'
        });
      } catch (error) {
        console.error('Ошибка при отправке запроса скидки:', error);
        this.$bvToast.toast('Ошибка при отправке запроса скидки', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      }
    },
    
    askAboutServiceSelection() {
    //   if (!this.hasSelectedQuotation()) {
    //     this.$bvToast.toast('Необходимо выбрать утвержденную котировку перед переводом в выполнение', {
    //       title: 'Выберите котировку',
    //       variant: 'warning',
    //       solid: true
    //     });
    //     return;
    //   }

      this.$emit('makeOrderSupply', this.order_id);
      
      // Эмитим событие для обновления данных в дочерних компонентах
      setTimeout(() => {
        this.$emit("orderUpdated", {
            orderId: this.order_id,
            message: 'Статус заказа обновлен!'
        });
      }, 2000);
    },

    hasSelectedQuotation() {
      const quotationAnswerForms = this.$refs.quotationAnswerForms;
      
      if (!quotationAnswerForms) {
        return false;
      }

      const answers = quotationAnswerForms.answers || [];
      return answers.some(answer => answer.status === 'selected');
    },
    
    async changeToQuotationReceivedStatus() {
      try {
        const updateFields = {
          reference_status_id: 542,
          order_service_id: this.order_service_id
        };

        await this.updateAnyOrder(this.order_id, updateFields);
        
        this.$emit("orderUpdated", {
          orderId: this.order_id,
          message: 'Статус заказа изменен на "Котировка получена"'
        });

        this.$bvToast.toast('Статус заказа успешно изменен на "Котировка получена"', {
          title: 'Успех',
          variant: 'success',
          solid: true
        });

      } catch (error) {
        console.error('Ошибка при смене статуса:', error);
        this.$bvToast.toast('Ошибка при смене статуса заказа', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      }
    }
  }
};
</script>
